import { IconBuildingStore, IconPower } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
  CampaignType,
  SponsoredBrandsEligibility,
  SponsoredDisplayAvailability,
} from "@/components/campaign-creation/wizard/campaign-type-availability";
import { WizardFooter } from "@/components/campaign-creation/wizard/WizardFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Field, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

interface ProfileOption {
  shopId: string;
  shopName: string;
  marketplaceName: string;
}

interface StepOneProps {
  isLoading: boolean;
  profiles: ProfileOption[];
  selectedShopId: string | null;
  onSelectShop: (shopId: string) => void;
  onReconnect: () => void;
  onRetrySponsoredDisplay?: () => void;
  selectedCampaignType: CampaignType;
  sponsoredBrandsEligibility: SponsoredBrandsEligibility;
  sponsoredDisplayAvailability: SponsoredDisplayAvailability;
  onSelectCampaignType: (campaignType: CampaignType) => void;
}

export function CampaignCreationStepOneScreen({
  onDiscard,
  onNext,
  ...stepOneProps
}: StepOneProps & {
  onDiscard: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <>
      <h1 className="mb-5 text-center text-2xl font-semibold tracking-tight text-ink">
        {t("stepOne.title")}
      </h1>
      <StepOne {...stepOneProps} />
      <WizardFooter
        step={1}
        canContinueStepOne={Boolean(
          !stepOneProps.selectedShopId ||
            stepOneProps.selectedCampaignType === "sp" ||
            (stepOneProps.selectedCampaignType === "sb" &&
              stepOneProps.sponsoredBrandsEligibility === "available") ||
            (stepOneProps.selectedCampaignType === "sd" &&
              stepOneProps.sponsoredDisplayAvailability === "available"),
        )}
        onBack={() => undefined}
        onContinue={() => {
          if (!stepOneProps.selectedShopId) {
            toast.error(t("validation.shopRequired"));
            return;
          }
          onNext();
        }}
        onDiscard={onDiscard}
      />
    </>
  );
}

export function StepOne({
  isLoading,
  profiles,
  selectedShopId,
  onSelectShop,
  onReconnect,
  onRetrySponsoredDisplay,
  selectedCampaignType,
  sponsoredBrandsEligibility,
  sponsoredDisplayAvailability,
  onSelectCampaignType,
}: StepOneProps) {
  const { t } = useTranslation("campaignCreation");
  const isDemo = useAuthStore((state) => state.user?.deploymentMode === "demo");
  if (isLoading) return <Skeleton className="h-80" />;
  if (profiles.length === 0)
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <IconBuildingStore />
          </EmptyMedia>
          <EmptyTitle>{t("profile.empty.title")}</EmptyTitle>
          <EmptyDescription>{t("profile.empty.description")}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button type="button" onClick={onReconnect}>
            {t("profile.empty.action")}
          </Button>
        </EmptyContent>
      </Empty>
    );
  const items = [
    { value: null, label: t("profile.placeholder") },
    ...profiles.map((profile) => ({
      value: profile.shopId,
      label: `${profile.shopName} · ${profile.marketplaceName}`,
    })),
  ];
  const campaignTypes = [
    {
      value: "sp",
      imageSrc: "/images/campaign-types/sponsored-products.png",
      title: t("campaignType.sp.title"),
      englishTitle: t("campaignType.sp.englishTitle"),
      description: t("campaignType.sp.description"),
    },
    {
      value: "sb",
      imageSrc: "/images/campaign-types/sponsored-brands.png",
      title: t("campaignType.sb.title"),
      englishTitle: t("campaignType.sb.englishTitle"),
      description: t("campaignType.sb.description"),
      disabled: isDemo || (Boolean(selectedShopId) && sponsoredBrandsEligibility !== "available"),
      unavailableReason: isDemo
        ? t("campaignType.demoUnavailable")
        : sponsoredBrandsEligibility === "idle"
          ? t("campaignType.sb.selectProfile")
          : sponsoredBrandsEligibility === "checking"
            ? t("campaignType.sb.checking")
            : sponsoredBrandsEligibility === "unavailable"
              ? t("campaignType.sb.unavailable")
              : undefined,
    },
    {
      value: "sd",
      imageSrc: "/images/campaign-types/sponsored-display.png",
      title: t("campaignType.sd.title"),
      englishTitle: t("campaignType.sd.englishTitle"),
      description: t("campaignType.sd.description"),
      disabled: isDemo || (Boolean(selectedShopId) && sponsoredDisplayAvailability !== "available"),
      unavailableReason: isDemo
        ? t("campaignType.demoUnavailable")
        : sponsoredDisplayAvailability === "idle"
          ? t("campaignType.sd.selectProfile")
          : sponsoredDisplayAvailability === "checking"
            ? t("campaignType.sd.checking")
            : sponsoredDisplayAvailability === "available"
              ? undefined
              : sponsoredDisplayAvailability === "unsupported"
                ? t("campaignType.sd.unsupported")
                : t("campaignType.sd.unavailable"),
    },
  ];
  return (
    <div className="flex flex-col gap-3">
      <Card className="border-0 py-5 shadow-sm">
        <CardContent
          data-slot="campaign-creation-shop-selector"
          className="mx-auto w-full max-w-2xl"
        >
          <Field className="gap-3">
            <FieldLabel>
              <span aria-hidden="true" className="text-destructive-foreground">
                *
              </span>
              {t("profile.label")}
            </FieldLabel>
            <Select
              items={items}
              value={selectedShopId}
              onValueChange={(value) => value && onSelectShop(value)}
            >
              <SelectTrigger size="lg" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {items.map((item) => (
                    <SelectItem key={item.value ?? "empty"} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>
      <FieldSet>
        <FieldLegend className="sr-only">{t("campaignType.title")}</FieldLegend>
        <ToggleGroup
          value={[selectedCampaignType]}
          onValueChange={(value) => {
            const campaignType = value[0];
            if (campaignType === "sp" || campaignType === "sb" || campaignType === "sd") {
              onSelectCampaignType(campaignType);
            }
          }}
          aria-label={t("campaignType.title")}
          className="grid w-full grid-cols-3 items-stretch gap-5"
        >
          {campaignTypes.map((campaignType) => (
            <CampaignTypeCard
              key={campaignType.value}
              {...campaignType}
              selected={campaignType.value === selectedCampaignType}
            />
          ))}
        </ToggleGroup>
      </FieldSet>
      {selectedShopId && !isDemo && sponsoredDisplayAvailability === "unavailable" ? (
        <div className="flex justify-end">
          <Button type="button" size="sm" variant="outline" onClick={onRetrySponsoredDisplay}>
            {t("actions.retry")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function CampaignTypeCard({
  value,
  imageSrc,
  title,
  englishTitle,
  description,
  selected,
  disabled,
  unavailableReason,
}: {
  value: string;
  imageSrc: string;
  title: string;
  englishTitle: string;
  description: string;
  selected: boolean;
  disabled?: boolean;
  unavailableReason?: string;
}) {
  const { t } = useTranslation("campaignCreation");
  const showEnglishTitle = englishTitle !== title;
  return (
    <ToggleGroupItem
      value={value}
      disabled={disabled}
      aria-label={title}
      className={cn(
        "h-auto min-h-[300px] w-full min-w-0 flex-col justify-between gap-0 overflow-hidden rounded-xl border bg-card p-0 text-card-foreground whitespace-normal shadow-sm transition-[border-color,box-shadow,transform] hover:bg-card aria-pressed:bg-card disabled:opacity-100",
        selected
          ? "border-primary shadow-[0_0_0_2px_color-mix(in_oklab,var(--primary)_20%,transparent),var(--paper-card-shadow)]"
          : "border-border",
      )}
    >
      <span className="flex w-full flex-1 flex-col items-center px-6 pt-5 text-center">
        <span className="text-lg font-semibold leading-snug">{title}</span>
        {showEnglishTitle ? (
          <span className="mt-1 text-base font-medium leading-snug">{englishTitle}</span>
        ) : null}
        <img
          src={imageSrc}
          alt=""
          className={cn(
            "mx-auto h-[105px] w-full object-contain object-[57%_center]",
            showEnglishTitle ? "mt-3" : "mt-4",
          )}
          draggable={false}
        />
        <span className="mt-2 max-w-sm text-sm font-normal leading-[18px] text-foreground">
          {description}
        </span>
        {unavailableReason ? (
          <span className="mt-2 max-w-sm text-xs leading-4 text-muted-foreground">
            {unavailableReason}
          </span>
        ) : null}
      </span>
      <span className="mt-4 flex w-full items-center justify-center bg-muted/35 py-2.5">
        <span
          className={cn(
            "grid size-9 place-items-center rounded-full bg-card shadow-sm",
            selected ? "text-primary" : "text-muted-foreground/35",
          )}
        >
          <IconPower />
          <span className="sr-only">
            {selected
              ? t("campaignType.selected")
              : disabled
                ? t("campaignType.unavailable")
                : t("campaignType.available")}
          </span>
        </span>
      </span>
    </ToggleGroupItem>
  );
}
